/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { Address, parseUnits } from "viem";
import { TokenList } from "../store/global/getAllTokenLists";
import { Amount } from "../types/amount";
import { getTokenAddressBySymbol } from "../utils/orderUtils";
import {
  MswChain,
  MswPosition,
  MswQuoteSearchParams,
  MswQuoteTokenFieldResponse,
  MswQuoteType,
  MswToken,
} from "./data/snapshot";
import tokenData from "./data/tokens";

export function mswGetTokenData(
  chain: MswChain,
  symbol: MswToken
): { decimals: number; usdRate: Amount } {
  // @ts-expect-error `symbol` is not a safe way to access the JSON file
  const decimals: number = tokenData[chain].tokens[symbol].chainInfo.decimals;
  const usdRate = new Amount(
    // @ts-expect-error `symbol` is not a safe way to access the JSON file
    (tokenData[chain].tokens[symbol].priceUsd as number).toString()
  );
  return { decimals, usdRate };
}

export function mswGetQuoteType(searchParams: MswQuoteSearchParams): `${MswQuoteType}` {
  if ("buy_tokens_ratios" in searchParams) {
    return MswQuoteType.OneToManyPercentages;
  }
  if (searchParams.sell_tokens.length > 1) {
    return MswQuoteType.ManyToOne;
  }
  return MswQuoteType.OneToOne;
}

export function mswGetTokenSymbols(
  addresses: Array<Address>,
  tokens: TokenList,
  network: MswChain
): Array<MswToken> {
  const tokenSymbols = addresses.map(address => {
    const symbol = Object.keys(tokens).find(value => {
      const token = value as MswToken;
      // @ts-expect-error MswToken type is a subset of Token type
      return address === getTokenAddressBySymbol(tokens, network, token);
    }) as MswToken | undefined;
    if (symbol === undefined) throw new Error("Invalid token in MSW");
    return symbol;
  });
  return tokenSymbols;
}

export function mswGetRate(nominator: Amount, denominator: Amount): Amount {
  return nominator.div(denominator);
}

export function mswGetQuoteTokenFieldResponse(
  position: MswPosition,
  type: `${MswQuoteType}`,
  chain: MswChain,
  tokens: TokenList | undefined,
  tokenSymbols: Array<MswToken>,
  counterTokenSymbols: Array<MswToken>,
  sellAmounts: Array<number>,
  buyTokenRatios?: Array<number> | undefined
): MswQuoteTokenFieldResponse {
  const response: MswQuoteTokenFieldResponse = {};
  // Buy: 'rate' prop only present in 121 and 12M quotes
  // Sell: 'rate' prop only present in 121 and M21 quotes
  const counterTokenData =
    type === MswQuoteType.OneToOne ||
    (position === MswPosition.Sell && type === MswQuoteType.ManyToOne) ||
    (position === MswPosition.Buy && type === MswQuoteType.OneToManyPercentages)
      ? mswGetTokenData(chain, counterTokenSymbols[0])
      : null;

  let amounts: Array<number> | null = position === MswPosition.Sell ? sellAmounts : null;
  if (position === MswPosition.Buy) {
    amounts = getBuyAmounts(chain, tokenSymbols, counterTokenSymbols, sellAmounts, buyTokenRatios);
  }
  if (amounts === null) {
    throw new Error(
      "`buyTokenRatios` property needs to be passed in if `position === MswPosition.Buy`"
    );
  }

  tokenSymbols.forEach((symbol, i) => {
    const { decimals, usdRate } = mswGetTokenData(chain, symbol);
    const amount = (() => {
      try {
        return parseUnits(amounts![i].toString(), decimals);
      } catch (_) {
        // Hacky way to derive rough amount if amounts[i] < 0
        return BigInt(Math.floor(10 ** decimals * amounts![i]));
      }
    })();
    const priceUsd = getPriceUsd(new Amount(amounts![i].toString()), usdRate);

    response[symbol] = {
      amount: amount.toString(),
      amountUsd: parseFloat(priceUsd.toString()),
      // @ts-expect-error MswToken type is a subset of Token type, MswChain type is a subset of Chain type
      contractAddress: getTokenAddressBySymbol(tokens, chain, symbol),
      priceUsd: parseFloat(usdRate.toString()),
      decimals,
      ...(counterTokenData && {
        rate: parseFloat(mswGetRate(usdRate, counterTokenData.usdRate).toString()),
      }),
    };
  });
  return response;
}

export function getPriceUsd(amount: Amount, usdRate: Amount) {
  return amount.mul(usdRate);
}

export function getBuyAmounts(
  chain: MswChain,
  buyTokenSymbols: Array<MswToken>,
  sellTokenSymbols: Array<MswToken>,
  sellAmounts: Array<number>,
  buyTokenRatios?: Array<number>
): Array<number> {
  const totalSellUsd = sellTokenSymbols.reduce((prev, symbol, i) => {
    const { usdRate } = mswGetTokenData(chain, symbol);
    const sellAmount = new Amount(sellAmounts[i].toString());
    return prev.add(getPriceUsd(sellAmount, usdRate));
  }, new Amount("0"));

  const buyAmounts = buyTokenSymbols.map((symbol, i) => {
    const { usdRate } = mswGetTokenData(chain, symbol);

    if (buyTokenRatios && buyTokenSymbols.length > 1) {
      const ratio = new Amount(buyTokenRatios[i].toString());
      const buyUsd = totalSellUsd.mul(ratio);
      return parseFloat(buyUsd.div(usdRate).toString());
    }

    return parseFloat(totalSellUsd.div(usdRate).toString());
  });

  return buyAmounts;
}
